import React, { useState } from "react";
import { Box, Button, Paper, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Print } from "@material-ui/icons";
import swal from "sweetalert2";
import { IGuiaCreateResult } from "../../entities/guias/IGuiaCreateResult";
import { b64toBlob } from "../../blocks/utility";
import { traducirLabel, traducirMessage } from "../../blocks/traductor";
import useGuiaPdf, { GUIA_PDF_IMPRESION_REIMPRESION } from "../../hooks/useGuiaPdf";
import useGuiaEtiquetasPdf from "../../hooks/useGuiaEtiquetasPdf";
import LoadingDialog from "../controls/LoadingDialog";
import RevisionGuiaResumenView from "./RevisionGuiaResumenView";

const useStyles = makeStyles(() =>
    createStyles({
        mainTitle: {
            color: "#19754e",
            fontSize: "18px",
            fontWeight: "bold",
            textAlign: "center"
        },
        guiaNumero: {
            color: "#000",
            textDecoration: "underline",
            fontWeight: 700,
            fontSize: "22px",
            textAlign: "center",
            marginTop: 8,
            marginBottom: 16
        },
        actions: {
            display: "flex",
            justifyContent: "center",
            gap: 16,
            marginBottom: 16,
            flexWrap: "wrap"
        }
    })
);

interface OwnProps {
    createResult: IGuiaCreateResult;
}

type Props = OwnProps;

const PostgeneracionGuiaView = ({ createResult }: Props) => {
    const classes = useStyles();
    const { obtenerPdfBase64 } = useGuiaPdf();
    const { obtenerEtiquetasPdfBase64 } = useGuiaEtiquetasPdf();
    const [loadingPdf, setLoadingPdf] = useState(false);
    const [loadingEtiquetas, setLoadingEtiquetas] = useState(false);
    const [etiquetasImpresasEnSesion, setEtiquetasImpresasEnSesion] = useState(false);

    const etiquetasYaImpresas =
        etiquetasImpresasEnSesion ||
        createResult.fechaImpresionEtiqueta != null;

    const descargarPdfDesdeBase64 = (base64: string, nombreArchivo: string) => {
        const blob = b64toBlob(base64, "application/pdf");
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = nombreArchivo;
        link.click();
        URL.revokeObjectURL(blobUrl);
    };

    const handlePdf = async () => {
        if (loadingPdf || loadingEtiquetas) {
            return;
        }
        setLoadingPdf(true);
        try {
            const response = await obtenerPdfBase64(createResult.guiaId, GUIA_PDF_IMPRESION_REIMPRESION);
            descargarPdfDesdeBase64(response, `Guia-${createResult.numGuia}.pdf`);
        } catch {
            swal.fire({
                titleText: traducirMessage(276),
                text: traducirMessage(215),
                type: "error"
            });
        } finally {
            setLoadingPdf(false);
        }
    };

    const handleEtiquetas = async () => {
        if (loadingPdf || loadingEtiquetas || etiquetasYaImpresas) {
            return;
        }
        setLoadingEtiquetas(true);
        try {
            const response = await obtenerEtiquetasPdfBase64(createResult.guiaId);
            descargarPdfDesdeBase64(response, `GuiaEtiquetas-${createResult.numGuia}.pdf`);
            setEtiquetasImpresasEnSesion(true);
        } catch {
            swal.fire({
                titleText: traducirMessage(276),
                text: traducirMessage(215),
                type: "error"
            });
        } finally {
            setLoadingEtiquetas(false);
        }
    };

    return (
        <>
            <Paper>
                <Box px={3} pt={3} pb={1}>
                    <Typography className={classes.mainTitle}>
                        {traducirLabel(354)}
                    </Typography>
                    <Typography className={classes.guiaNumero} component="div">
                        {createResult.numGuia}
                    </Typography>
                    {createResult.timbradoExitoso === false && createResult.timbradoMensaje && (
                        <Box px={3} pb={2}>
                            <Alert severity="warning">{createResult.timbradoMensaje}</Alert>
                        </Box>
                    )}
                    <Box className={classes.actions}>
                        <Button
                            color="primary"
                            onClick={handlePdf}
                            disabled={loadingPdf || loadingEtiquetas}
                            startIcon={<Print />}
                        >
                            {traducirLabel(164)}
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleEtiquetas}
                            disabled={loadingPdf || loadingEtiquetas || etiquetasYaImpresas}
                            startIcon={<Print />}
                        >
                            {traducirLabel(162)}
                        </Button>
                    </Box>
                </Box>
                <RevisionGuiaResumenView
                    titulo=""
                    createResult={createResult}
                    ocultarEncabezadoSuperior={true}
                />
            </Paper>
            {(loadingPdf || loadingEtiquetas) && (
                <LoadingDialog titulo={traducirLabel(240)} abierto={true} />
            )}
        </>
    );
};

export default PostgeneracionGuiaView;
